// Plain-language result of one scored test, shown right after it on the session console.

import type { TestInfo } from "../api";

const EYE: Record<string, string> = { OD: "Right eye", OS: "Left eye", OU: "Both eyes" };

export function snellen(logmar: number): { feet: string; metres: string } {
  return { feet: `20/${Math.round(20 * 10 ** logmar)}`, metres: `6/${Math.round(6 * 10 ** logmar)}` };
}

export interface ResultText {
  title: string;
  /** The score, e.g. "0.20 logMAR". */
  value: string | null;
  /** What it means, e.g. "about 20/32 (6/9.5)". */
  detail: string;
  counted: boolean;
  notes: string[];
}

export function resultText(t: TestInfo): ResultText | null {
  const r = t.result;
  if (!r) return null;
  const title = `${EYE[t.eye] ?? t.eye} — ${t.kind === "acuity" ? "visual acuity" : "contrast sensitivity"}`;
  const notes: string[] = [];
  if (r.demo) notes.push("Debug mode: the screen checks were skipped, so this result is approximate and not part of the record.");
  if (r.confirm_requested) notes.push("This is quite different from earlier results, so the test is done once more to check.");
  if (r.alert === "contact") notes.push("Changed from earlier results twice in a row.");
  const counted = t.status === "complete" && r.valid !== false;
  if (!counted) {
    const why = (r.invalid_reasons ?? []).join("; ") || (t.status === "aborted" ? "the test was stopped" : "a check failed");
    return { title, value: null, detail: `Not counted: ${why}.`, counted: false, notes };
  }
  if (t.kind === "acuity") {
    if (typeof r.logmar !== "number") return { title, value: null, detail: "No score.", counted, notes };
    const s = snellen(r.logmar);
    return { title, value: `${r.logmar.toFixed(2)} logMAR`, detail: `about ${s.feet} (${s.metres})`, counted, notes };
  }
  if (r.letter_logmar === null || r.letter_logmar === undefined) {
    return { title, value: null, detail: "The letters were not read at full contrast, even at the largest size.", counted, notes };
  }
  const modulation = r.measure === "log_modulation";
  if (typeof r.log_cs !== "number") {
    return { title, value: modulation ? "below 0.00" : "below 0.00 log CS",
      detail: "The full-contrast letters were read, but not the faintest level.", counted, notes };
  }
  const pct = 100 * 10 ** -r.log_cs;
  return modulation
    ? { title, value: `${r.log_cs.toFixed(2)} log modulation`,
      detail: `faintest letters seen: ${pct.toFixed(1)}% of the way from the background to the letter colour`, counted, notes }
    : { title, value: `${r.log_cs.toFixed(2)} log CS`, detail: `faintest letters seen: ${pct.toFixed(1)}% contrast`, counted, notes };
}

/** The test scored most recently (repeats and checks are created, and scored, later). */
export function latestScored(tests: TestInfo[]): TestInfo | null {
  const done = tests.filter((t) => t.result && (t.status === "complete" || t.status === "aborted"));
  return done.length ? done.reduce((a, b) => (b.id > a.id ? b : a)) : null;
}
